import React from "react";
import styled, { withTheme } from "styled-components";
import DefaultTable, {
  ActionButtons,
  Line,
  ExpandableLine,
} from "../../ui-library/CustomTable/DefaultTable";
import { Dropdown } from "react-bootstrap";
import ButtonDropdown from "../../ui-library/ButtonDropdown/ButtonDropdown";
import {
  DietDairyTypes,
  dietDairyTypeToString,
  getGlycosadeNutritionItems,
} from "../../smart/DietDiary/DietDiary.constants";
import { FoodType, studiesIds } from "../../../modules/constants";
import { TRIHEPTANOIN_NUTRITION_ITEMS } from "../../smart/DietDiary/DietDiary.constants";
import TableDropDown from "../../ui-library/TableDropDown/TableDropDown";
import { useTranslation } from "react-i18next";
import { decimalPlaces, roundMealValue } from "../../../helpers/mealsHelper";

const CompleteIcon = styled.i`
  color: ${(props) => props?.theme?.lightGreen};
  font-size: 20px;
`;

const StudyEntriesTable = (props) => {
  const {
    getData,
    searchedParams,
    totalitems,
    entries,
    editMeal,
    viewMeal,
    canCreateEntriesMealLog,
    canEditEntriesMealLog,
    canViewDetailMealLog,
    canCreateChangeRequestMealLog,
    addNewMeal,
    study,
    loading,
  } = props;

  const { t } = useTranslation();

  const defaultProps = {
    headers: [
      { label: t("appText.date"), key: "Header1" },
      { label: t("appText.complete") },
      { label: t("appText.dietDiary.entries") },
      { label: t("appText.time") },
      { label: t("appText.description") },
      { label: t("appText.dietDiary.calories") },
      { label: t("appText.dietDiary.protein") },
      { label: t("appText.dietDiary.carbohydrates") },
      { label: t("appText.dietDiary.fats") },
      { label: t("appText.dietDiary.sugars") },
      { label: t("appText.options") },
    ],
    maincolor: "#8cc13c",
    totalitems,
    hasSearch: 0,
    haspagesizefilter: 1,
    haspagination: 1,
    searchedParams,
    getData,
    isExpandable: true,
    loading,
  };

  const getDropDownItems = (entry) => {
    const dropDownMenuItems = {
      VIEW_DETAILS: {
        text: t("appText.viewDetails"),
        callBack: (meal) => viewMeal(meal),
        id: 1,
        isVisible: canViewDetailMealLog,
      },
      EDIT: {
        text: t("appText.edit"),
        callBack: (meal) => editMeal(meal),
        id: 2,
        isVisible:
          study.toLocaleLowerCase() === studiesIds.gsdiii.toLocaleLowerCase()
            ? canCreateChangeRequestMealLog && isCompleted(entry)
            : canEditEntriesMealLog,
      },
    };
    return Object.values(dropDownMenuItems);
  };

  const dietDiaryOptions = {
    FOOD_DRINK: {
      id: "3",
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.FOOD_DRINK
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
      type: DietDairyTypes.FOOD_DRINK,
      isVisible: true,
      onClick: () => {
        addNewMeal(dietDiaryOptions.FOOD_DRINK);
      },
    },
    TRIHEPTANOIN: {
      id: "6",
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.TRIHEPTANOIN
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.TRIHEPTANOIN),
      type: DietDairyTypes.TRIHEPTANOIN,
      isVisible:
        study.toLocaleLowerCase() === studiesIds.lcfaod401.toLocaleLowerCase(),
      defaultItems: [
        {
          foodType: FoodType.CUSTOM,
          nutritionItems: TRIHEPTANOIN_NUTRITION_ITEMS,
        },
      ],
      onClick: () => {
        addNewMeal(dietDiaryOptions.TRIHEPTANOIN);
      },
    },
    MCTOIL: {
      id: "5",
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.MCT_OIL
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.MCT_OIL),
      type: DietDairyTypes.MCT_OIL,
      isVisible:
        study.toLocaleLowerCase() === studiesIds.lcfaod401.toLocaleLowerCase(),
      defaultItems: [
        {
          foodType: FoodType.COMMON,
          title: "MCT Oil",
          canRemove: true,
        },
      ],
      onClick: () => {
        addNewMeal(dietDiaryOptions.MCTOIL);
      },
    },
    CORNSTARCH: {
      id: "2",
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.CORNSTARCH
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.CORNSTARCH),
      type: DietDairyTypes.CORNSTARCH,
      isVisible:
        study.toLocaleLowerCase() === studiesIds.gsdiii.toLocaleLowerCase(),
      defaultItems: [
        {
          foodType: FoodType.BRANDED,
          nixItemId: "51d376c9cc9bff5553aa95ee",
          canRemove: true,
        },
      ],
      onClick: () => {
        addNewMeal(dietDiaryOptions.CORNSTARCH);
      },
    },
    GLYCOSADE: {
      id: "4",
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.GLYCOSADE
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.GLYCOSADE),
      type: DietDairyTypes.GLYCOSADE,
      isVisible:
        study.toLocaleLowerCase() === studiesIds.gsdiii.toLocaleLowerCase(),
      defaultItems: [
        {
          foodType: FoodType.CUSTOM,
          nutritionItems: getGlycosadeNutritionItems(),
        },
      ],
      onClick: () => {
        addNewMeal(dietDiaryOptions.GLYCOSADE);
      },
    },
  };

  const isCompleted = (entry) => {
    return entry.meals.find((meal) => meal.isCompleted);
  };

  return (
    <DefaultTable {...defaultProps}>
      <ActionButtons>
        {canCreateEntriesMealLog && (
          <ButtonDropdown
            size="sm"
            title="Add new"
            options={Object.values(dietDiaryOptions)}
          />
        )}
      </ActionButtons>
      {entries?.map((entry, i) => (
        <ExpandableLine key={`expandable-${entry}-${i}`}>
          <Line key={`parent-${entry}-${i}`}>
            <b>
              <span>{entry?.date}</span>
            </b>
            <span>
              {isCompleted(entry) ? (
                <CompleteIcon className="fas fa-check-circle" />
              ) : (
                "-"
              )}
            </span>
            <span>{entry?.meals.length}</span>
            <span>-</span>
            <span>-</span>
            <span>
              {Number(roundMealValue(entry?.calories.value)).toFixed(
                decimalPlaces
              )}{" "}
              {entry?.calories.unit}
            </span>
            <span>
              {Number(roundMealValue(entry?.protein.percentage)).toFixed(
                decimalPlaces
              )}
              % -{" "}
              {Number(roundMealValue(entry?.protein.value)).toFixed(
                decimalPlaces
              )}{" "}
              {entry?.protein.unit}
            </span>
            <span>
              {Number(roundMealValue(entry?.carbohydrates.percentage)).toFixed(
                decimalPlaces
              )}
              % -{" "}
              {Number(roundMealValue(entry?.carbohydrates.value)).toFixed(
                decimalPlaces
              )}{" "}
              {entry?.carbohydrates.unit}
            </span>
            <span>
              {Number(roundMealValue(entry?.fats.percentage)).toFixed(
                decimalPlaces
              )}
              % -{" "}
              {Number(roundMealValue(entry?.fats.value)).toFixed(decimalPlaces)}{" "}
              {entry?.fats.unit}
            </span>
            <span>
              {Number(roundMealValue(entry?.sugars.value)).toFixed(
                decimalPlaces
              )}{" "}
              {entry?.sugars.unit}
            </span>
            <span>-</span>
          </Line>
          {entry?.meals?.map((child, k) => (
            <Line key={`child-${child}-${k}`}>
              <span>-</span>
              <span>-</span>
              <span>-</span>
              <span>{child?.time}</span>
              <span>{child?.description}</span>
              <span>
                {Number(roundMealValue(child?.calories.value)).toFixed(
                  decimalPlaces
                )}{" "}
                {child?.calories.unit}
              </span>
              <span>
                {Number(roundMealValue(child?.protein.percentage)).toFixed(
                  decimalPlaces
                )}
                % -{" "}
                {Number(roundMealValue(child?.protein.value)).toFixed(
                  decimalPlaces
                )}{" "}
                {child?.protein.unit}
              </span>
              <span>
                {Number(
                  roundMealValue(child?.carbohydrates.percentage)
                ).toFixed(decimalPlaces)}
                % -{" "}
                {Number(roundMealValue(child?.carbohydrates.value)).toFixed(
                  decimalPlaces
                )}{" "}
                {child?.carbohydrates.unit}
              </span>
              <span>
                {Number(roundMealValue(child?.fats.percentage)).toFixed(
                  decimalPlaces
                )}
                % -{" "}
                {Number(roundMealValue(child?.fats.value)).toFixed(
                  decimalPlaces
                )}{" "}
                {child?.fats.unit}
              </span>
              <span>
                {Number(roundMealValue(child?.sugars.value)).toFixed(
                  decimalPlaces
                )}{" "}
                {child?.sugars.unit}
              </span>

              <TableDropDown>
                {getDropDownItems(entry)
                  .filter((dropDownItem) => dropDownItem.isVisible)
                  .map((item, index) => (
                    <Dropdown.Item
                      key={index}
                      bsPrefix="dropdown-item"
                      onSelect={() => item.callBack(child)}
                    >
                      {item.text}
                    </Dropdown.Item>
                  ))}
              </TableDropDown>
            </Line>
          ))}
        </ExpandableLine>
      ))}
    </DefaultTable>
  );
};

export default withTheme(StudyEntriesTable);
